//@ts-nocheck
import { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';

import {
	BxpWebStargateClient,
	useBxpWebStargateClient,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	UpdateConsentPreferencesRequest,
	LocaleType,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

export type Consent = {
	key: string;
	displayedText: string;
	granted: boolean;
};

export type SubscriptionKey = string;

export type Subscription = {
	key: SubscriptionKey;
	subscribed: boolean;
};

type SubsciberDetails = { [key: string]: string };

type UpdatePreferencesPayload = {
	readonly formId: string;
	readonly site?: string;
	readonly source?: string;
	readonly email: string;
	readonly locale: string;
	readonly formUrl: string;
	readonly subscriberDetails?: SubsciberDetails;
	readonly consentLabelText?: string;
	readonly consentCheckboxValue: boolean;
	readonly privacyPolicyText?: string;
	readonly ndaText?: string;
};

const requiredProps = ['email', 'site', 'locale', 'formUrl'];

const defaultLocaleConfig: LocaleType = {
	locale: 'UNKNOWN',
	localeRequiresMarketingCommunicationOptIn: true,
};

export const requestConfig = async (stargateClient: BxpWebStargateClient) => {
	return await stargateClient.getConsentConfig().then(
		(response) => response,
		(err) => {
			return defaultLocaleConfig;
		},
	);
};

export const updatePreferences = async (
	stargateClient: BxpWebStargateClient,
	params: UpdateConsentPreferencesRequest,
	localeConfig?: LocaleType,
) => {
	const paramsLocale = params.paramsLocale ? params.paramsLocale : 'UNKNOWN';
	const postData: UpdatePreferencesPayload = {
		locale: localeConfig.locale || paramsLocale,
		...params,
	};

	if (!requiredProps.every((prop) => postData.hasOwnProperty(prop))) {
		throw `Invalid parameters. Required: {${requiredProps.toString()}}`;
	}

	const updateConsentPreferencesResponseStatus =
		await stargateClient.updateConsentPreferences(postData);
	return updateConsentPreferencesResponseStatus;
};

const useGetConsentConfig = () => {
	const stargateClient = useBxpWebStargateClient();
	const [localeConfig, setLocaleConfig] = useState(defaultLocaleConfig);

	useEffect(() => {
		const fetchConfigData = async () => {
			const consentHubConfig = await requestConfig(stargateClient);
			if (consentHubConfig) {
				setLocaleConfig(consentHubConfig);
			}
		};

		fetchConfigData().catch((err) => console.log(err));
	}, [stargateClient]);

	return [localeConfig];
};

export const ConsentHubClientContext = createContext<LocaleType | null>(defaultLocaleConfig);

type ConsentHubClientConfigProviderProps = PropsWithChildren;

export const ConsentHubClientConfigProvider = ({
	children,
}: ConsentHubClientConfigProviderProps) => {
	const [localeConfig] = useGetConsentConfig();
	return (
		<ConsentHubClientContext.Provider value={localeConfig}>
			{children}
		</ConsentHubClientContext.Provider>
	);
};

export const useConsentHubClientConfig = () => {
	const context = useContext(ConsentHubClientContext);
	if (!context) {
		throw new Error(
			'useConsentHubClientConfig must be used within a ConsentHubClientConfigProvider',
		);
	}
	return context;
};
